// useDatabaseMissionsState.js

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Toast
import { toast } from 'react-toastify';

// Services
import { updateNodeTypeMissions } from 'services/nodetypes/NodeTypeMutationService';
import {
  listBaseWorkflowTemplates,
  syncBaseWorkflowTemplates,
} from 'services/workflows/BaseWorkflowTemplateService';
import { requestWorkflowAccomplishmentPhrases } from 'services/workflows/WorkflowAccomplishmentService';

// Utilities
import { generateKey } from 'utilities/Keys';
import {
  normalizeMissionTask,
} from 'missions/taskmodal/missionTaskModalUtils';
import {
  buildLegacyMissionTemplateKey,
  buildMissionTemplatesPayload,
  normalizeMissionTemplate,
  normalizeMissionTemplateList,
} from 'missions/missionTemplateUtils';
import {
  applyWorkflowCompleteSpineFromTaskOrder,
  normalizeWorkflowEdges,
} from 'workflows/workflowGraphUtils';
import { getNextMissionTemplatePosition } from 'utilities/MissionTemplateOrder';

const toKeyString = (value) => String(value || '').trim();

const buildMissionTemplateKey = (typeKey) => {
  const normalizedTypeKey = toKeyString(typeKey);
  const suffix = generateKey();
  return normalizedTypeKey ? `mission:${normalizedTypeKey}:${suffix}` : `mission:${suffix}`;
};

const normalizeMissionTemplateForSave = (template) => {
  const normalized = normalizeMissionTemplate(template);
  const numericPosition = Number(template?.position);

  return {
    description: normalized.description,
    edges: normalized.edges,
    iconKey: normalized.iconKey || 'account_tree',
    key: normalized.key,
    ...(Number.isFinite(numericPosition) ? { position: numericPosition } : {}),
    tasks: normalized.tasks,
    title: normalized.title,
  };
};

const areMissionTemplatesEqual = (left, right) => {
  return JSON.stringify((Array.isArray(left) ? left : []).map((template) => normalizeMissionTemplateForSave(template)))
    === JSON.stringify((Array.isArray(right) ? right : []).map((template) => normalizeMissionTemplateForSave(template)));
};

/**
 * useDatabaseMissionsState
 *
 * Manages Workflow template drafts for the active node type.
 * Custom types persist on the type document; system types use base-scoped
 * `_workflowTemplates` (same product shape as Views/Automations).
 *
 * @param {object} params - Hook parameters.
 * @param {object|null} params.activeDatabase - Active database document data.
 * @param {object|null} params.typeDef - Active node type definition.
 * @param {boolean} params.isOpen - Whether the parent database modal is open.
 * @param {function} params.onSaved - Applies the persisted patch back to local parent state.
 * @param {string|null} params.baseKey - Active base key for mutations / base overlays.
 *
 * @returns {object} Workflow tab state and mutation handlers.
 */
export default function useDatabaseMissionsState({
  activeDatabase,
  typeDef = null,
  isOpen,
  onSaved,
  typeKey = '',
  baseKey = null,
}) {
  const [missionTemplates, setMissionTemplates] = useState([]);
  const [savedMissionTemplates, setSavedMissionTemplates] = useState([]);
  const [selectedMissionTemplateKey, setSelectedMissionTemplateKey] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [missionTemplateEditorOpen, setMissionTemplateEditorOpenState] = useState(false);
  const [missionTemplateEditorMode, setMissionTemplateEditorMode] = useState('create');
  const [missionEditorOpen, setMissionEditorOpen] = useState(false);
  const [editingMissionTask, setEditingMissionTask] = useState(null);
  const [missionDesignOpen, setMissionDesignOpen] = useState(false);
  const [missionMetaError, setMissionMetaError] = useState('');
  const lastInitializedTypeKeyRef = useRef('');
  const savedBaseTemplatesRef = useRef([]);
  const missionTemplatesRef = useRef([]);
  const designSaveChainRef = useRef(Promise.resolve());

  useEffect(() => {
    missionTemplatesRef.current = missionTemplates;
  }, [missionTemplates]);

  const resolvedTypeKey = useMemo(() => {
    return String(typeKey || typeDef?.key || activeDatabase?.type || '').trim();
  }, [activeDatabase?.type, typeDef?.key, typeKey]);

  const isSystemType = typeDef?.system === true || typeDef?.isSystem === true;
  const usesBaseScopedTemplates = isSystemType === true;

  const sourceMissions = useMemo(() => {
    return typeDef?.missions ?? activeDatabase?.missions ?? null;
  }, [activeDatabase?.missions, typeDef?.missions]);

  const selectedMissionTemplate = useMemo(() => {
    return missionTemplates.find((template) => template.key === selectedMissionTemplateKey) || null;
  }, [missionTemplates, selectedMissionTemplateKey]);

  const missionTasks = useMemo(() => {
    return Array.isArray(selectedMissionTemplate?.tasks) ? selectedMissionTemplate.tasks : [];
  }, [selectedMissionTemplate?.tasks]);

  const missionEdges = useMemo(() => {
    return normalizeWorkflowEdges(selectedMissionTemplate?.edges);
  }, [selectedMissionTemplate?.edges]);

  const missionTitle = String(selectedMissionTemplate?.title || '');
  const missionDescription = String(selectedMissionTemplate?.description || '');
  const missionIconKey = String(selectedMissionTemplate?.iconKey || 'account_tree').trim() || 'account_tree';

  const canSaveMissionMeta = useMemo(() => {
    return Boolean(selectedMissionTemplate)
      && missionTitle.trim().length > 0
      && !areMissionTemplatesEqual(missionTemplates, savedMissionTemplates)
      && !isSaving;
  }, [isSaving, missionTemplates, missionTitle, savedMissionTemplates, selectedMissionTemplate]);

  const updateSelectedMissionTemplate = useCallback((updater) => {
    if (!selectedMissionTemplateKey) return;

    setMissionTemplates((prev) => prev.map((template) => {
      if (template.key !== selectedMissionTemplateKey) return template;

      const nextTemplate = typeof updater === 'function' ? updater(template) : template;
      return {
        ...normalizeMissionTemplateForSave(nextTemplate),
        description: String(nextTemplate?.description || ''),
        iconKey: String(nextTemplate?.iconKey || 'account_tree').trim() || 'account_tree',
        title: String(nextTemplate?.title || ''),
      };
    }));
  }, [selectedMissionTemplateKey]);

  useEffect(() => {
    if (!isOpen) {
      lastInitializedTypeKeyRef.current = '';
      return undefined;
    }

    if (usesBaseScopedTemplates) {
      const scopeKey = `${String(baseKey || '').trim()}::${resolvedTypeKey}`;
      if (lastInitializedTypeKeyRef.current === scopeKey) {
        return undefined;
      }

      lastInitializedTypeKeyRef.current = scopeKey;
      setMissionTemplateEditorOpenState(false);
      setMissionTemplateEditorMode('create');
      setMissionEditorOpen(false);
      setEditingMissionTask(null);
      setIsSaving(false);
      setMissionMetaError('');

      if (!String(baseKey || '').trim() || !resolvedTypeKey) {
        setMissionTemplates([]);
        setSavedMissionTemplates([]);
        savedBaseTemplatesRef.current = [];
        setSelectedMissionTemplateKey('');
        return undefined;
      }

      let cancelled = false;
      listBaseWorkflowTemplates({ baseKey, nodeType: resolvedTypeKey })
        .then((rows) => {
          if (cancelled) return;
          const nextMissionTemplates = (Array.isArray(rows) ? rows : [])
            .map((template) => normalizeMissionTemplateForSave(template))
            .filter((template) => template.key);
          savedBaseTemplatesRef.current = nextMissionTemplates;
          setMissionTemplates(nextMissionTemplates);
          setSavedMissionTemplates(nextMissionTemplates);
          setSelectedMissionTemplateKey(nextMissionTemplates[0]?.key || '');
        })
        .catch((error) => {
          if (cancelled) return;
          console.error('[useDatabaseMissionsState] base workflow load failed:', error);
          toast.error(error?.message || 'Could not load playbooks.');
          setMissionTemplates([]);
          setSavedMissionTemplates([]);
          savedBaseTemplatesRef.current = [];
          setSelectedMissionTemplateKey('');
        });

      return () => {
        cancelled = true;
      };
    }

    if (lastInitializedTypeKeyRef.current === resolvedTypeKey) {
      return undefined;
    }

    const legacyTemplateKey = buildLegacyMissionTemplateKey(resolvedTypeKey);
    const nextMissionTemplates = normalizeMissionTemplateList(sourceMissions, {
      legacyTemplateKey,
    }).map((template) => normalizeMissionTemplateForSave(template));
    const nextSelectedKey = nextMissionTemplates[0]?.key || '';

    lastInitializedTypeKeyRef.current = resolvedTypeKey;

    setMissionTemplates(nextMissionTemplates);
    setSavedMissionTemplates(nextMissionTemplates);
    setSelectedMissionTemplateKey(nextSelectedKey);
    setMissionTemplateEditorOpenState(false);
    setMissionTemplateEditorMode('create');
    setMissionEditorOpen(false);
    setEditingMissionTask(null);
    setIsSaving(false);
    setMissionMetaError('');
    return undefined;
  }, [baseKey, isOpen, resolvedTypeKey, sourceMissions, usesBaseScopedTemplates]);

  useEffect(() => {
    if (!selectedMissionTemplateKey && missionTemplates.length > 0) {
      setSelectedMissionTemplateKey(missionTemplates[0].key);
      return;
    }

    if (selectedMissionTemplateKey && !missionTemplates.some((template) => template.key === selectedMissionTemplateKey)) {
      setSelectedMissionTemplateKey(missionTemplates[0]?.key || '');
    }
  }, [missionTemplates, selectedMissionTemplateKey]);

  /**
   * persistMissionTemplates
   *
   * Saves the full Mission template list to the active database document.
   *
   * @param {object[]} nextTemplates - Next normalized template list to persist.
   * @param {string} successMessage - Toast copy shown when save succeeds.
   * @param {boolean} showSuccessToast - Whether to show a success toast.
   * @returns {Promise<boolean>} Whether the save succeeded.
   */
  const persistMissionTemplates = useCallback(async (nextTemplates, successMessage, showSuccessToast = true) => {
    if (!resolvedTypeKey) {
      toast.error('Missing playbook save context.');
      return false;
    }

    const normalizedTemplates = (Array.isArray(nextTemplates) ? nextTemplates : [])
      .map((template) => normalizeMissionTemplateForSave(template))
      .filter((template) => template.key);

    try {
      setIsSaving(true);
      setMissionMetaError('');

      if (usesBaseScopedTemplates) {
        if (!baseKey) {
          toast.error('Missing base for playbook save.');
          return false;
        }

        const saved = await syncBaseWorkflowTemplates({
          baseKey,
          nodeType: resolvedTypeKey,
          templates: normalizedTemplates,
          previousTemplates: savedBaseTemplatesRef.current,
        });
        const nextSaved = saved.map((template) => normalizeMissionTemplateForSave(template));
        savedBaseTemplatesRef.current = nextSaved;
        setMissionTemplates(nextSaved);
        setSavedMissionTemplates(nextSaved);
        onSaved?.({
          missions: {
            templates: nextSaved,
            version: 2,
            source: 'base',
          },
        });
      } else {
        const payload = buildMissionTemplatesPayload(normalizedTemplates);
        await updateNodeTypeMissions({
          missions: payload.missions,
          typeKey: resolvedTypeKey,
          baseKey,
        });
        setMissionTemplates(normalizedTemplates);
        setSavedMissionTemplates(normalizedTemplates);
        onSaved?.(payload);
        requestWorkflowAccomplishmentPhrases({
          baseKey,
          typeKey: resolvedTypeKey,
        });
      }

      if (showSuccessToast && successMessage) {
        toast.success(successMessage);
      }
      return true;
    } catch (error) {
      toast.error(error?.message || 'Could not save playbooks.');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [baseKey, onSaved, resolvedTypeKey, usesBaseScopedTemplates]);

  /**
   * handleSaveMissionMeta
   *
   * Persists the selected Mission template metadata.
   *
   * @returns {Promise<boolean>} Whether the save succeeded.
   */
  const handleSaveMissionMeta = useCallback(async () => {
    if (!selectedMissionTemplateKey) return false;

    const cleanedTitle = String(selectedMissionTemplate?.title || '').trim();
    const cleanedDescription = String(selectedMissionTemplate?.description || '');

    if (!resolvedTypeKey) {
      toast.error('Missing playbook save context.');
      return false;
    }

    if (!cleanedTitle) {
      setMissionMetaError('Mission title is required.');
      return false;
    }

    const nextTemplates = missionTemplates.map((template) => {
      if (template.key !== selectedMissionTemplateKey) return template;

      return {
        ...template,
        description: cleanedDescription,
        title: cleanedTitle,
      };
    });

    return persistMissionTemplates(nextTemplates, 'Mission template updated.');
  }, [missionTemplates, persistMissionTemplates, resolvedTypeKey, selectedMissionTemplate, selectedMissionTemplateKey]);

  const handleSelectMissionTemplate = useCallback((templateKey) => {
    const normalizedTemplateKey = toKeyString(templateKey);
    if (!normalizedTemplateKey) return;

    setSelectedMissionTemplateKey(normalizedTemplateKey);
    setMissionMetaError('');
  }, []);

  /**
   * setMissionTemplateEditorOpen
   *
   * Toggles the Mission template editor and clears empty unsaved template drafts when closing.
   *
   * @param {boolean} nextOpen - Whether the editor should be open.
   */
  const setMissionTemplateEditorOpen = useCallback((nextOpen) => {
    if (nextOpen) {
      setMissionTemplateEditorOpenState(true);
      return;
    }

    const selectedTemplate = missionTemplates.find((template) => template.key === selectedMissionTemplateKey) || null;
    const isPersistedTemplate = savedMissionTemplates.some((template) => template.key === selectedMissionTemplateKey);
    const isEmptyDraft = selectedTemplate
      && !String(selectedTemplate?.title || '').trim()
      && !String(selectedTemplate?.description || '').trim()
      && (!Array.isArray(selectedTemplate?.tasks) || selectedTemplate.tasks.length === 0);

    setMissionTemplateEditorOpenState(false);
    setMissionTemplateEditorMode('create');
    setMissionMetaError('');

    if (selectedTemplate && !isPersistedTemplate && isEmptyDraft) {
      setMissionTemplates((prev) => prev.filter((template) => template.key !== selectedTemplate.key));
    }
  }, [missionTemplates, savedMissionTemplates, selectedMissionTemplateKey]);

  const handleAddMissionTemplate = useCallback(() => {
    const nextTemplate = normalizeMissionTemplateForSave({
      description: '',
      iconKey: 'account_tree',
      key: buildMissionTemplateKey(resolvedTypeKey),
      position: getNextMissionTemplatePosition(missionTemplates),
      tasks: [],
      title: 'Untitled Playbook',
    });

    setMissionTemplates((prev) => [...prev, nextTemplate]);
    setSelectedMissionTemplateKey(nextTemplate.key);
    setMissionTemplateEditorMode('create');
    setMissionTemplateEditorOpenState(false);
    setMissionDesignOpen(true);
    setMissionEditorOpen(false);
    setEditingMissionTask(null);
    setMissionMetaError('');
  }, [missionTemplates, resolvedTypeKey]);

  /**
   * handleEditMissionTemplate
   *
   * Opens the Mission template editor for the selected template.
   *
   * @param {object|null} template - Mission template to edit.
   */
  const handleEditMissionTemplate = useCallback((template) => {
    const templateKey = toKeyString(template?.key);
    if (!templateKey) return;

    setSelectedMissionTemplateKey(templateKey);
    setMissionTemplateEditorMode('edit');
    setMissionTemplateEditorOpenState(true);
    setMissionMetaError('');
  }, []);

  const handleDesignMissionTemplate = useCallback((template = null) => {
    const templateKey = toKeyString(template?.key || selectedMissionTemplateKey);
    if (!templateKey) return;

    setSelectedMissionTemplateKey(templateKey);
    setMissionTemplateEditorOpenState(false);
    setMissionDesignOpen(true);
    setMissionMetaError('');
  }, [selectedMissionTemplateKey]);

  /**
   * closeMissionDesign
   *
   * Closes the designer and drops empty unsaved workflow drafts created via Add.
   */
  const closeMissionDesign = useCallback(() => {
    setMissionDesignOpen(false);

    const selectedTemplate = missionTemplates.find(
      (template) => template.key === selectedMissionTemplateKey
    ) || null;
    if (!selectedTemplate) return;

    const isPersistedTemplate = savedMissionTemplates.some(
      (template) => template.key === selectedMissionTemplateKey
    );
    const title = String(selectedTemplate?.title || '').trim();
    const description = String(selectedTemplate?.description || '').trim();
    const taskCount = Array.isArray(selectedTemplate?.tasks) ? selectedTemplate.tasks.length : 0;
    const isEmptyDraft = !isPersistedTemplate
      && taskCount === 0
      && !description
      && (!title || title === 'Untitled Playbook');

    if (!isEmptyDraft) return;

    const nextTemplates = missionTemplates.filter(
      (template) => template.key !== selectedMissionTemplateKey
    );
    setMissionTemplates(nextTemplates);
    setSelectedMissionTemplateKey(nextTemplates[0]?.key || '');
    setMissionMetaError('');
  }, [missionTemplates, savedMissionTemplates, selectedMissionTemplateKey]);

  const handleSaveMissionDesign = useCallback(async (nextTemplate, options = {}) => {
    const templateKey = toKeyString(nextTemplate?.key || selectedMissionTemplateKey);
    if (!templateKey) return false;

    const shouldClose = options.close !== false;
    const silent = options.silent === true;

    const runSave = async () => {
      const normalized = normalizeMissionTemplate(nextTemplate);
      const nextTemplates = missionTemplatesRef.current.map((template) => (
        template.key === templateKey
          ? {
            ...template,
            ...normalized,
            key: templateKey,
          }
          : template
      ));

      const didSave = await persistMissionTemplates(
        nextTemplates,
        silent ? '' : 'Playbook design saved.',
        !silent,
      );
      if (didSave && shouldClose) {
        setMissionDesignOpen(false);
      }
      return didSave;
    };

    // Serialize design writes so rapid autosaves cannot clobber each other.
    const result = designSaveChainRef.current.then(runSave, runSave);
    designSaveChainRef.current = result.then(() => undefined, () => undefined);
    return result;
  }, [persistMissionTemplates, selectedMissionTemplateKey]);

  const handleDeleteMissionTemplate = useCallback(async (templateKey) => {
    const normalizedTemplateKey = toKeyString(templateKey || selectedMissionTemplateKey);
    if (!normalizedTemplateKey) return false;

    const nextTemplates = missionTemplates.filter((template) => template.key !== normalizedTemplateKey);
    const didDelete = await persistMissionTemplates(nextTemplates, 'Playbook deleted.');

    if (didDelete) {
      setSelectedMissionTemplateKey(nextTemplates[0]?.key || '');
      setMissionTemplateEditorMode('create');
      setMissionTemplateEditorOpenState(false);
      setMissionDesignOpen(false);
      setEditingMissionTask(null);
      setMissionEditorOpen(false);
    }

    return didDelete;
  }, [missionTemplates, persistMissionTemplates, selectedMissionTemplateKey]);

  const handleAddMissionTask = useCallback(() => {
    if (!selectedMissionTemplateKey) return;
    if (!String(selectedMissionTemplate?.title || '').trim()) {
      setMissionMetaError('Mission title is required before adding tasks.');
      return;
    }

    setEditingMissionTask(null);
    setMissionEditorOpen(true);
  }, [selectedMissionTemplate, selectedMissionTemplateKey]);

  const handleEditMissionTask = useCallback((task) => {
    if (!task || !selectedMissionTemplateKey) return;
    setEditingMissionTask(task);
    setMissionEditorOpen(true);
  }, [selectedMissionTemplateKey]);

  const handleSaveMissionTask = useCallback(async (nextTask, options = {}) => {
    if (!selectedMissionTemplateKey) return false;
    if (!String(selectedMissionTemplate?.title || '').trim()) {
      setMissionMetaError('Mission title is required before adding tasks.');
      return false;
    }

    const normalizedTask = normalizeMissionTask({
      ...nextTask,
      key: String(nextTask?.key || '').trim() || generateKey(),
    });
    const existingIndex = missionTasks.findIndex((task) => task.key === normalizedTask.key);
    const nextTasks = existingIndex >= 0
      ? missionTasks.map((task, index) => index === existingIndex ? normalizedTask : task)
      : [...missionTasks, normalizedTask];

    let nextEdges = Array.isArray(options?.edges)
      ? normalizeWorkflowEdges(options.edges)
      : normalizeWorkflowEdges(selectedMissionTemplate?.edges);

    // New tasks: list order owns the default Complete spine. Edits keep author routes.
    if (existingIndex < 0) {
      nextEdges = applyWorkflowCompleteSpineFromTaskOrder(nextEdges, nextTasks);
    }

    const nextTemplates = missionTemplates.map((template) => {
      if (template.key !== selectedMissionTemplateKey) return template;
      return {
        ...template,
        edges: nextEdges,
        tasks: nextTasks,
      };
    });

    const didSave = await persistMissionTemplates(
      nextTemplates,
      existingIndex >= 0 ? 'Step updated.' : 'Step created.'
    );

    if (didSave) {
      setEditingMissionTask(null);
      setMissionEditorOpen(false);
    }

    return didSave;
  }, [missionTasks, missionTemplates, persistMissionTemplates, selectedMissionTemplate, selectedMissionTemplateKey]);

  const handleDeleteMissionTask = useCallback(async (taskKey) => {
    if (!selectedMissionTemplateKey) return false;

    const normalizedKey = String(taskKey || '').trim();
    if (!normalizedKey) return false;

    const nextTasks = missionTasks.filter((task) => task.key !== normalizedKey);
    const prunedEdges = normalizeWorkflowEdges(selectedMissionTemplate?.edges).filter((edge) => (
      edge.fromTaskKey !== normalizedKey && edge.toTaskKey !== normalizedKey
    ));
    const nextEdges = applyWorkflowCompleteSpineFromTaskOrder(prunedEdges, nextTasks);
    const nextTemplates = missionTemplates.map((template) => {
      if (template.key !== selectedMissionTemplateKey) return template;
      return {
        ...template,
        edges: nextEdges,
        tasks: nextTasks,
      };
    });
    const didDelete = await persistMissionTemplates(nextTemplates, 'Step deleted.');

    if (didDelete) {
      setEditingMissionTask(null);
      setMissionEditorOpen(false);
    }

    return didDelete;
  }, [missionTasks, missionTemplates, persistMissionTemplates, selectedMissionTemplate, selectedMissionTemplateKey]);

  const handleReorderMissionTask = useCallback(async (draggedTaskKey, targetTaskKey) => {
    if (!selectedMissionTemplateKey) return;
    if (!String(selectedMissionTemplate?.title || '').trim()) {
      setMissionMetaError('Mission title is required before reordering tasks.');
      throw new Error('Mission title is required before reordering tasks.');
    }

    const draggedIndex = missionTasks.findIndex((task) => task.key === draggedTaskKey);
    const targetIndex = missionTasks.findIndex((task) => task.key === targetTaskKey);
    if (draggedIndex < 0 || targetIndex < 0 || draggedIndex === targetIndex) return;

    const nextTasks = [...missionTasks];
    const [movedTask] = nextTasks.splice(draggedIndex, 1);
    nextTasks.splice(targetIndex, 0, movedTask);

    const nextEdges = applyWorkflowCompleteSpineFromTaskOrder(
      selectedMissionTemplate?.edges,
      nextTasks,
    );

    const nextTemplates = missionTemplates.map((template) => {
      if (template.key !== selectedMissionTemplateKey) return template;
      return {
        ...template,
        edges: nextEdges,
        tasks: nextTasks,
      };
    });
    const didSave = await persistMissionTemplates(nextTemplates, '', false);
    if (!didSave) {
      throw new Error('Could not reorder steps.');
    }
  }, [missionTasks, missionTemplates, persistMissionTemplates, selectedMissionTemplate, selectedMissionTemplateKey]);

  /**
   * handleReorderMissionTemplate
   *
   * Persists a reordered Mission template list.
   *
   * @param {string} draggedTemplateKey - Template key being dragged.
   * @param {string} targetTemplateKey - Template key receiving the drop.
   * @returns {Promise<void>} Resolves when the reorder has persisted.
   */
  const handleReorderMissionTemplate = useCallback(async (draggedTemplateKey, targetTemplateKey) => {
    const draggedIndex = missionTemplates.findIndex((template) => template.key === draggedTemplateKey);
    const targetIndex = missionTemplates.findIndex((template) => template.key === targetTemplateKey);
    if (draggedIndex < 0 || targetIndex < 0 || draggedIndex === targetIndex) return;

    const nextTemplates = [...missionTemplates];
    const [movedTemplate] = nextTemplates.splice(draggedIndex, 1);
    nextTemplates.splice(targetIndex, 0, movedTemplate);
    const reorderedTemplates = nextTemplates.map((template, index) => ({
      ...template,
      position: index * 100,
    }));

    const didSave = await persistMissionTemplates(reorderedTemplates, '', false);
    if (!didSave) {
      throw new Error('Could not reorder playbooks.');
    }
  }, [missionTemplates, persistMissionTemplates]);

  return {
    missionTemplates,
    selectedMissionTemplateKey,
    missionTasks,
    isSaving,
    missionTemplateEditorOpen,
    missionTemplateEditorMode,
    missionTitle,
    missionDescription,
    missionIconKey,
    missionMetaError,
    canSaveMissionMeta,
    setMissionTemplateEditorOpen,
    missionEditorOpen,
    setMissionEditorOpen,
    missionDesignOpen,
    setMissionDesignOpen,
    closeMissionDesign,
    editingMissionTask,
    missionEdges,
    selectedMissionTemplate,
    setMissionTitle: (nextTitle) => updateSelectedMissionTemplate((template) => ({
      ...template,
      title: String(nextTitle || ''),
    })),
    setMissionDescription: (nextDescription) => updateSelectedMissionTemplate((template) => ({
      ...template,
      description: String(nextDescription || ''),
    })),
    setMissionIconKey: (nextIconKey) => updateSelectedMissionTemplate((template) => ({
      ...template,
      iconKey: String(nextIconKey || '').trim() || 'account_tree',
    })),
    handleSaveMissionMeta,
    handleSelectMissionTemplate,
    handleAddMissionTemplate,
    handleEditMissionTemplate,
    handleDesignMissionTemplate,
    handleSaveMissionDesign,
    handleDeleteMissionTemplate,
    handleReorderMissionTemplate,
    handleAddMissionTask,
    handleEditMissionTask,
    handleSaveMissionTask,
    handleDeleteMissionTask,
    handleReorderMissionTask,
  };
}
